import * as mobx from 'mobx';
import { transaction } from '@src/slide/domain/slide-app';
import _has from 'lodash/has';
import StyleHandler from '@src/slide/domain/ui-model/adapter/style-handler';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { mergeNoConflictProperty } from '@src/slide/domain/ui-model/element-modeles/property/utils/merge-property';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import {
    EFFECT_KEY, IEffect, EEffectType, IEffectUpdateRemove, TEffectVal, IEffectModelValCollection,
    IGlowModelVal, IReflectionModelVal, ISoftEdgeModelVal, IShadowModelVal,
} from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';

/**
 * 效果操作类
 */
export class Effect implements IEffect {
    /**
     * 整合sdk需要的效果更新格式
     * @param effectValCollection 效果集合
     * @private
     */
    public static getEffectUpdateRemove(effectValCollection: IEffectModelValCollection | null): IEffectUpdateRemove {
        const remove = { style: { effect: {} } };
        [EEffectType.GLOW, EEffectType.REFLECTION, EEffectType.SOFTEDGE].forEach((effectType) => {
            const value = effectValCollection?.[effectType];
            if (_has(effectValCollection, effectType) && Effect.isNullOrEmptyObj(value)) {
                remove.style.effect[effectType] = null;
            }
        });
        if (_has(effectValCollection, EEffectType.SHADOW)) {
            const value = effectValCollection?.[EEffectType.SHADOW];
            remove.style.effect[EEffectType.SHADOW] = Effect.isNullOrEmptyObj(value)
                ? null : { isOuter: !value?.isOuter };
        }
        return {
            update: { style: { effect: effectValCollection } },
            remove: Object.keys(remove.style.effect).length ? remove : null,
        };
    }

    /**
     * 传入的值是否为null或者空对象（如果是则代表要置为无效果）
     * @param effectVal 效果值
     * @private
     */
    private static isNullOrEmptyObj(effectVal?: TEffectVal | null): boolean {
        return effectVal === null || Object.keys(effectVal || {}).length === 0;
    }

    /**
     * 操作的图形 uiModel：基本图形、多选图形
     * @private
     */
    private readonly model: IElementBaseModel | IMultiplyModel;

    /**
     * 多选图形、组合的属性是综合所有子元素而来的，基于性能考虑，加入缓存，只在必要时重新获取更新缓存
     * @private
     */
    private mergePropertyValCache: IEffectModelValCollection | null;

    public constructor(model: IElementBaseModel | IMultiplyModel) {
        this.model = model;
        this.mergePropertyValCache = null;
    }

    /**
     * 获取效果值 - 发光
     */
    public getGlow(): IGlowModelVal | null {
        return this.getEffectCollection()?.[EEffectType.GLOW] || null;
    }

    /**
     * 获取效果值 - 映像
     */
    public getReflection(): IReflectionModelVal | null {
        return this.getEffectCollection()?.[EEffectType.REFLECTION] || null;
    }

    /**
     * 获取效果值 - 柔化边缘
     */
    public getSoftEdge(): ISoftEdgeModelVal | null {
        return this.getEffectCollection()?.[EEffectType.SOFTEDGE] || null;
    }

    /**
     * 获取效果值 - 阴影
     */
    public getShadow(): IShadowModelVal | null {
        return this.getEffectCollection()?.[EEffectType.SHADOW] || null;
    }

    /**
     * 更新效果值 - 发光
     * @param glow 发光值
     * @param onlyChangeUI  是否为实时预览（只更新ui，不保存到sdk）
     */
    public updateGlow(glow: IGlowModelVal | null, onlyChangeUI: boolean): void {
        return this.updateEffectCollection({ [EEffectType.GLOW]: glow }, onlyChangeUI);
    }

    /**
     * 更新效果值 - 映像
     * @param reflection 映像值
     * @param onlyChangeUI  是否为实时预览（只更新ui，不保存到sdk）
     */
    public updateReflection(reflection: IReflectionModelVal | null, onlyChangeUI: boolean): void {
        return this.updateEffectCollection({ [EEffectType.REFLECTION]: reflection }, onlyChangeUI);
    }

    /**
     * 更新效果值 - 柔化边缘
     * @param softEdge 柔化边缘值
     * @param onlyChangeUI 是否为实时预览（只更新ui，不保存到sdk）
     */
    public updateSoftEdge(softEdge: ISoftEdgeModelVal | null, onlyChangeUI: boolean): void {
        return this.updateEffectCollection({ [EEffectType.SOFTEDGE]: softEdge }, onlyChangeUI);
    }

    /**
     * 更新效果值 - 阴影
     * @param shadow 阴影值
     * @param onlyChangeUI 是否为实时预览（只更新ui，不保存到sdk）
     */
    public updateShadow(shadow: IShadowModelVal | null, onlyChangeUI: boolean): void {
        return this.updateEffectCollection({ [EEffectType.SHADOW]: shadow }, onlyChangeUI);
    }

    /**
     * 获取效果值 - 效果集合
     */
    public getEffectCollection(): IEffectModelValCollection | null {
        if ([UIModelType.GROUP, UIModelType.MULTIPLY].includes(this.model.type as UIModelType)) {
            return this.mergePropertyValCache;
        }
        return this.model.props.style[EFFECT_KEY] || null;
    }

    /**
     * 更新效果值 - 效果集合
     * @param effectValCollection 效果集合
     * @param onlyChangeUI 是否为实时预览（只更新ui，不保存到sdk）
     */
    public updateEffectCollection(effectValCollection: IEffectModelValCollection | null, onlyChangeUI: boolean): void {
        if ([UIModelType.GROUP, UIModelType.MULTIPLY].includes(this.model.type as UIModelType)) {
            let children: IElementBaseModel[] = [];
            if (this.model.type === UIModelType.MULTIPLY) {
                children = (this.model as IMultiplyModel).selectedModels;
            } else if (this.model.type === UIModelType.GROUP) {
                children = (this.model as IGroupModel).children as IElementBaseModel[];
            }
            // 连接线效果渲染有问题，目前单选连接线侧边栏不展示效果设置入口，多选时屏蔽数据更新
            children = children.filter((item: IElementBaseModel) => item.type !== UIModelType.CONNECTOR);
            mobx.transaction(() => {
                transaction(() => {
                    this.updateMultiple(children, effectValCollection, onlyChangeUI);
                });
            });
        } else {
            this.updateSingle(this.model, effectValCollection, onlyChangeUI);
        }
    }

    /**
     * 刷新 多选图形|组合 合并后的效果值
     */
    public refreshMergePropertyValCache(): IEffectModelValCollection | null {
        if ([UIModelType.GROUP, UIModelType.MULTIPLY].includes(this.model.type as UIModelType)) {
            const mergeModel = this.model as (IGroupModel | IMultiplyModel);
            // 连接线效果渲染有问题，多选时过滤连接线效果属性
            const getPropertyFunc = model => (model.type === UIModelType.CONNECTOR ? {}
                : { [EFFECT_KEY]: model.getEffect?.().getEffectCollection() });
            const uiModelPropertyObj = mergeNoConflictProperty(mergeModel, getPropertyFunc);
            this.mergePropertyValCache = uiModelPropertyObj[EFFECT_KEY] || null;
        }
        return this.mergePropertyValCache;
    }

    /**
     * 批量更新uiModel的效果值
     * @param models uiModel数组
     * @param effectValCollection 效果集合
     * @param onlyChangeUI 是否为实时预览（只更新ui，不保存到sdk）
     * @private
     */
    private updateMultiple(
        models,
        effectValCollection: IEffectModelValCollection | null,
        onlyChangeUI: boolean,
    ): void {
        models.forEach((model) => {
            model.getEffect().updateEffectCollection(effectValCollection, onlyChangeUI);
        });
    }

    /**
     * 更新单个uiModel的效果值
     * @param model uiModel
     * @param effectValCollection 效果集合
     * @param onlyChangeUI  是否为实时预览（只更新ui，不保存到sdk）
     * @private
     */
    private updateSingle(
        model,
        effectValCollection: IEffectModelValCollection | null,
        onlyChangeUI: boolean,
    ) {
        const params = Effect.getEffectUpdateRemove(effectValCollection);
        if (!params) {
            return;
        }
        if (onlyChangeUI) {
            // hover实时预览，不更新sdk
            StyleHandler.updateUiProps(params.update!, model);
            model.previewChange += 1; // 触发ui刷新
        } else {
            // 点击选择，更新sdk
            if (params.remove) {
                model.updateAndRemoveProps(params.update, params.remove);
            } else {
                model.updateProps(params.update);
            }
        }
    }
}
